import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, X, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATALOG_URL = "https://tigik.saubereko.eu/api/hof/skills-catalog";

export default function ConfirmedEditModal({ a, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    subject_name: a.subject_name || "", subject_email: a.subject_email || "", card_id: a.card_id || "", area: a.area || "",
    skills: a.skills || [], skills_other: a.skills_other || "",
  });
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(CATALOG_URL).then((r) => r.json()).then(setCatalog).catch(() => setCatalog({ groups: [] }));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setForm((f) => ({ ...f, skills: f.skills.includes(id) ? f.skills.filter((x) => x !== id) : [...f.skills, id] }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.patch(`${API}/admin/mentor-audits/${a.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Uloženie zlyhalo."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4" data-testid="confirmed-edit-modal" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-2xl bg-[#0a0a0a] border border-[#D4AF37]/30 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]">{a.record_no}</p>
            <h3 className="font-display text-xl">Upraviť údaje a zručnosti</h3>
          </div>
          <button onClick={onClose} data-testid="confirmed-edit-close" className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <In label="Meno a priezvisko" value={form.subject_name} onChange={set("subject_name")} testid="edit-subject-name" />
            <In label="E-mail absolventa" value={form.subject_email} onChange={set("subject_email")} testid="edit-subject-email" />
            <In label="Card ID" value={form.card_id} onChange={set("card_id")} testid="edit-card-id" />
            <In label="Oblasť / odbor" value={form.area} onChange={set("area")} testid="edit-area" />
          </div>
          <div data-testid="edit-skills">
            <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Zručnosti ({form.skills.length}) — rovnaký katalóg ako pri profesionáloch</p>
            {!catalog ? <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Načítavam katalóg…</p> : (
              <div className="space-y-4">
                {(catalog.groups || []).map((g) => (
                  <div key={g.id}>
                    <p className="text-[#D4AF37]/80 text-xs mb-2">{g.labels?.sk || g.id}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(g.skills || []).map((s) => {
                        const on = form.skills.includes(s.id);
                        return (
                          <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`edit-skill-${s.id}`}
                            className={`px-2.5 py-1 text-xs border transition-colors ${on ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F2E3C6]" : "border-white/15 text-white/55 hover:border-[#D4AF37]/50 hover:text-white"}`}>
                            {s.labels?.sk || s.id}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4"><In label="Iné zručnosti (voľný text)" value={form.skills_other} onChange={set("skills_other")} testid="edit-skills-other" /></div>
          </div>
          {err && <p className="text-red-400 text-sm" data-testid="confirmed-edit-error">{err}</p>}
          <p className="text-white/35 text-xs">Po uložení sa zmeny zapíšu do Siene slávy. Opravené PDF pošlete tlačidlom „Poslať znova".</p>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-white/60 hover:text-white">Zrušiť</button>
            <button onClick={save} disabled={busy} data-testid="confirmed-edit-save"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-2 text-sm hover:bg-[#e5c14f] transition-colors disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložiť
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const In = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="block text-xs text-white/50 mb-1.5">{label}</span>
    <input {...p} data-testid={testid} className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
  </label>
);
